import {ApiError} from "@variocube/app-ui";
import React, {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useAsync} from "react-async-hook";
import {useLocation, useNavigate} from "react-router-dom";
import {
	Booking,
	Feature,
	Permission,
	PERMISSIONS,
	Tenant,
	TenantSettings,
	UserPermissions,
	useTenantApi,
} from "./api";
import {getFlavorUrl} from "./domain";
import {Flavor, getFlavor} from "@smalox/shared";
import {LoginState} from "./LoginState";
import {useUser} from "./user";

interface TenantContextContent {
	tenant?: Tenant;
	settings?: TenantSettings;
	permissions: UserPermissions[];
	reload?: () => any;
	setPermissions: (permissions: UserPermissions[]) => void;
	features: Feature[];
}

const TenantContext = createContext<TenantContextContent>({
	permissions: [],
	setPermissions: () => void 0,
	features: [],
});

interface TenantProviderProps {
	tenantId: string;
}

export function TenantProvider({tenantId, children}: PropsWithChildren<TenantProviderProps>) {
	const {getTenant, getTenantSettings, getTenantPermissions, getTenantFeatures} = useTenantApi();
	const navigate = useNavigate();
	const location = useLocation();

	const [tenant, setTenant] = useState<Tenant>();
	const [settings, setSettings] = useState<TenantSettings>();
	const [permissions, setPermissions] = useState<UserPermissions[]>([]);
	const [features, setFeatures] = useState<Feature[]>([]);

	const {error, result, execute} = useAsync(
		(tenantId: string) =>
			Promise.all([
				getTenant(tenantId),
				getTenantSettings(tenantId),
				getTenantPermissions(tenantId),
				getTenantFeatures(tenantId),
			]),
		[tenantId],
	);

	useEffect(() => {
		if (result) {
			const [tenant, settings, permissions, features] = result;
			setTenant(tenant);
			setSettings(settings);
			setPermissions(permissions);
			setFeatures(features as Feature[] ?? []);
		}
	}, [result]);

	/**
	 * Domain redirect for flavor mismatch - INTENTIONAL BEHAVIOR
	 *
	 * When accessing a tenant via the "wrong" domain (e.g., a BoxLox tenant via smalox.com),
	 * we redirect to the correct flavor domain. This ensures users always see:
	 * - The correct theme/branding for the product variant
	 * - The correct terminology in the UI
	 * - URLs that match the tenant's flavor
	 *
	 * Uses a ref to ensure redirect only happens on initial tenant load, preventing:
	 * - Redirect loops when user updates flavor in settings
	 * - Loss of unsaved form state during flavor changes
	 */
	const hasCheckedFlavor = useRef(false);
	useEffect(() => {
		if (tenant?.flavor && !hasCheckedFlavor.current) {
			hasCheckedFlavor.current = true;
			const currentFlavor = getFlavor();
			const tenantFlavor = tenant.flavor as Flavor;

			// Validate the flavor value before using it
			if (tenantFlavor !== "SMALOX" && tenantFlavor !== "BOXLOX" && tenantFlavor !== "DAYLOX") {
				console.warn(`Unexpected tenant flavor: ${tenant.flavor}, skipping redirect`);
				return;
			}

			if (tenantFlavor !== currentFlavor) {
				const redirectUrl = getFlavorUrl(tenantFlavor, tenantId, location.pathname);
				window.location.href = redirectUrl;
			}
		}
	}, [tenant?.flavor, tenantId, location.pathname]);

	useEffect(() => {
		if (error instanceof ApiError && error.status == 404) {
			console.error("Error occurred while loading tenant:", error);
			navigate("/tenants");
		}
	}, [error, navigate]);

	const reload = useCallback(() => execute(tenantId), [execute, tenantId]);

	const value = useMemo(() => ({
		tenant,
		settings,
		reload,
		permissions,
		setPermissions,
		features,
	}), [tenant, settings, reload, permissions, setPermissions, features]);

	return (
		<LoginState loading={!tenant} error={error} tenantId={tenantId}>
			<TenantContext.Provider value={value}>
				{children}
			</TenantContext.Provider>
		</LoginState>
	);
}

export function useTenantId() {
	const {tenant} = useContext(TenantContext);
	if (!tenant) {
		throw new Error("No tenant found in context. You are probably calling this from outside a TenantProvider.");
	}
	return tenant.id;
}

/**
 * Returns the current tenant or undefined if not inside a TenantProvider.
 * Use this hook when you need to optionally access tenant data without throwing an error.
 */
export function useTenantOptional(): Tenant | undefined {
	const {tenant} = useContext(TenantContext);
	return tenant;
}

export function useTenantSettings() {
	const {settings} = useContext(TenantContext);
	if (!settings) {
		throw new Error("No tenant found in context. You are probably calling this from outside a TenantProvider.");
	}
	return settings;
}

export function useReloadTenant() {
	const {reload} = useContext(TenantContext);
	if (!reload) {
		throw new Error("No tenant found in context. You are probably calling this from outside a TenantProvider.");
	}
	return reload;
}

/**
 * Custom hook to retrieve the permissions for the current tenant from the context.
 * <b>Please keep in mind, that the location permissions are flattened, so each sub permission is
 * included as well, if you have permission for a parent location.</b>
 *
 * @return {Object} An object representing the permissions associated with the current tenant.
 */
export function usePermissions() {
	const {permissions} = useContext(TenantContext);
	return permissions;
}

export function useEmulatePermissions() {
	const {permissions, setPermissions} = useContext(TenantContext);
	const setEmulatedPermissions = useCallback((permissions: Permission[]) => {
		setPermissions([{
			location: undefined,
			permissions,
		}]);
	}, [setPermissions]);
	const emulatedPermissions = permissions.find(p => !p.location)?.permissions ?? PERMISSIONS;

	return [emulatedPermissions, setEmulatedPermissions] as const;
}

export function useTenantPermission(permission: Permission) {
	const permissions = usePermissions();
	return hasTenantPermission(permissions, permission);
}

export function useAnyPermission(...requestedPermissions: Permission[]) {
	const permissions = usePermissions();
	return hasAnyPermission(permissions, requestedPermissions);
}

export function useTenantPermissions(...requestedPermissions: Permission[]) {
	const permissions = usePermissions();
	return requestedPermissions.every(permission => hasTenantPermission(permissions, permission));
}

export function useTenantPermissionsFlat() {
	const permissions = usePermissions();

	const flattenedTenantPermissions = permissions
		.filter(role => !role.location)
		.flatMap(role => role.permissions ?? []);

	return Array.from(
		new Set(flattenedTenantPermissions),
	);
}

export function useCanManageBookingAccess(booking: Booking | undefined) {
	const permissions = usePermissions();
	const user = useUser();

	if (!booking) {
		return false;
	}

	return (booking.selfManaged && isBookingUser(booking, user.username))
		|| hasLocationPermission(permissions, "BOOKING_EDIT", booking.location.id);
}

function isBookingUser(booking: Booking, username: string) {
	return booking.users.some(bu => bu.user.username === username);
}

function hasTenantPermission(permissions: UserPermissions[], permission: Permission) {
	return permissions.some(role => !role.location && role.permissions?.includes(permission));
}

function hasAnyPermission(permissions: UserPermissions[], requestedPermissions: Permission[]) {
	return permissions.some(role => role.permissions?.some(permission => requestedPermissions.includes(permission)));
}

export function useLocationPermission(permission: Permission, locationId?: number) {
	const permissions = usePermissions();
	return hasLocationPermission(permissions, permission, locationId);
}

export function hasLocationPermission(permissions: UserPermissions[], permission: Permission, locationId?: number) {
	return permissions.some(role =>
		(!role.location || role.location.id == locationId) && role.permissions?.includes(permission)
	);
}

export function useTenantFeature(feature: Feature) {
	const {features} = useContext(TenantContext);
	return features.includes(feature);
}
